import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import { Layers, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submittingRef = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setErr("");
    setBusy(true);
    try {
      const res = await login(email, password, mfaRequired ? mfaCode : null);
      if (res?.mfaRequired) {
        setMfaRequired(true);
        toast.info("Enter your MFA code");
      } else {
        toast.success("Signed in");
        navigate(res.mfa_required_setup ? "/mfa-setup"
          : res.must_change_password ? "/change-password" : "/");
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 bg-slate-950 text-slate-100 relative overflow-hidden">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-white grid place-items-center">
            <Layers className="w-4 h-4 text-slate-900" />
          </div>
          <span className="font-semibold tracking-tight">RemediKeep</span>
        </div>
        <div>
          <h1 className="text-4xl lg:text-5xl tracking-tight font-semibold" style={{ fontFamily: "Manrope" }}>
            One CRM for every<br /> customer relationship.
          </h1>
          <p className="mt-4 text-sm text-slate-400 max-w-md leading-relaxed">
            Manage contacts, opportunities, and revenue across teams, branches, and brands —
            with the structure your business needs and the clarity your reps expect.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-10">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-2">
              <Lock className="w-3 h-3" /> Secure sign in
            </div>
            <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>Welcome back</h2>
            <p className="text-sm text-slate-500 mt-1">Enter your credentials to continue.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} required
              data-testid="login-email" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="password">Password</Label>
            <PasswordInput id="password" value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)} required
              data-testid="login-password" />
          </div>
          {mfaRequired && (
            <div className="space-y-1">
              <Label htmlFor="mfa">MFA code</Label>
              <Input id="mfa" inputMode="numeric" pattern="[0-9]*" value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                data-testid="login-mfa" placeholder="6-digit code" />
            </div>
          )}
          {err && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded-md" data-testid="login-error">
              {err}
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="login-submit">
            {busy ? "Signing in…" : mfaRequired ? "Verify MFA" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
